// cSpell:enableCompoundWords
import { ASLoader, ComponentSize, LoaderType } from '@eleventheye/asui';
import type { SeasonID } from '@shared/types';
import App from 'app/App';
import AuthIndexRoute from 'app/scenes/authentication/_index';
import { authLoader } from 'app/scenes/authentication/authLoader';
import Home from 'app/scenes/home/Home';
import CategoryRankingsIndexRoute from 'app/scenes/statsmanager/components/categoryrankings/_index';
import { categoryRankingsLoader } from 'app/scenes/statsmanager/components/categoryrankings/categoryRankingsLoader';
import ComparisonIndexRoute from 'app/scenes/statsmanager/components/comparison/_index';
import { comparisonLoader } from 'app/scenes/statsmanager/components/comparison/comparisonLoader';
import GameDetailsIndexRoute from 'app/scenes/statsmanager/components/gamedetails/_index';
import { gameDetailsLoader } from 'app/scenes/statsmanager/components/gamedetails/gameDetailsLoader';
import StandingsIndexRoute from 'app/scenes/statsmanager/components/standings/_index';
import { standingsLoader } from 'app/scenes/statsmanager/components/standings/standingsLoader';
import TeamIndexRoute from 'app/scenes/statsmanager/components/team/_index';
import Team from 'app/scenes/statsmanager/components/team/Team';
import { teamLoader } from 'app/scenes/statsmanager/components/team/teamLoader';
import TeamStatsIndexRoute from 'app/scenes/statsmanager/components/teamstats/_index';
import { teamStatsLoader } from 'app/scenes/statsmanager/components/teamstats/teamStatsLoader';
import ViewTeamIndexRoute from 'app/scenes/statsmanager/components/viewteam/_index';
import { viewTeamLoader } from 'app/scenes/statsmanager/components/viewteam/viewTeamLoader';
import WeeklyGamesIndexRoute from 'app/scenes/statsmanager/components/weeklygames/_index';
import { weeklyGamesLoader } from 'app/scenes/statsmanager/components/weeklygames/weeklyGamesLoader';
import { getBESyncSeasons } from 'app/slices/SyncManagerSlice';
import { getUserAuthState, UserAuthState } from 'app/slices/UserSlice';
import { store } from 'app/store';
import ErrorBoundary from 'components/errorboundary/ErrorBoundary';
import React from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppRoutes } from 'routes/Routes';
import { withValidRoute } from './navigation';

// Read at loader invocation, rather than capturing seasons when the router is created.
const getAvailableSeasons = (): SeasonID[] | undefined => {
  const state = store.getState();
  const seasons = getBESyncSeasons(state);
  return getUserAuthState(state) === UserAuthState.LoggedIn && seasons.length
    ? seasons.map(({ seasonID }) => seasonID)
    : undefined;
};

// #region LAZY LOADING *******************************************************

const Authentication = React.lazy(() =>
  import('app/scenes/authentication/Authentication').then((module) => ({
    default: module.Authentication,
  })),
);

const WeeklyGames = React.lazy(() =>
  import('app/scenes/statsmanager/components/weeklygames/WeeklyGames').then((module) => ({
    default: module.WeeklyGames,
  })),
);

const GameDetails = React.lazy(() =>
  import('app/scenes/statsmanager/components/gamedetails/GameDetails').then((module) => ({
    default: module.GameDetails,
  })),
);

const ViewTeam = React.lazy(() =>
  import('app/scenes/statsmanager/components/viewteam/ViewTeam').then((module) => ({
    default: module.ViewTeam,
  })),
);

// const Team = React.lazy(() =>
//   import('app/scenes/statsmanager/components/team/Team').then((module) => ({
//     default: module.Team,
//   }))
// );

const TeamStats = React.lazy(() =>
  import('app/scenes/statsmanager/components/teamstats/TeamStats').then((module) => ({
    default: module.TeamStats,
  })),
);

const CategoryRankings = React.lazy(() =>
  import('app/scenes/statsmanager/components/categoryrankings/CategoryRankings').then((module) => ({
    default: module.CategoryRankings,
  })),
);

// const ComparisonV2 = React.lazy(() =>
//   import('app/scenes/statsmanager/components/comparison/ComparisonV2').then((module) => ({
//     default: module.default,
//   })),
// );

const CompareTeams = React.lazy(() =>
  import('app/scenes/compareteams/CompareTeams').then((module) => ({
    default: module.default,
  })),
);

const Standings = React.lazy(() =>
  import('app/scenes/statsmanager/components/standings/Standings').then((module) => ({
    default: module.Standings,
  })),
);

const HeadToHeadScene = React.lazy(() =>
  import('app/scenes/headtoheadscene/HeadToHeadScene').then((module) => ({
    default: module.HeadToHeadScene,
  })),
);
// #endregion

const router = createBrowserRouter(
  [
    {
      id: 'route_app',
      element: <App />,
      path: AppRoutes.Home,
      errorElement: <ErrorBoundary debug={'From route_app'} />,
      hydrateFallbackElement: <ASLoader size={'ASLarge'} showLoader={true} loaderType={LoaderType.Spin} />,
      children: [
        {
          id: 'route_home',
          path: AppRoutes.Home,
          children: [
            {
              id: 'route_home_index',
              index: true,
              Component: Home,
            },
          ],
        },
        {
          id: 'route_auth',
          path: AppRoutes.Auth,
          children: [
            {
              id: 'route_auth_index',
              index: true,
              Component: AuthIndexRoute,
            },
            {
              id: 'route_auth_login_register_resetpassword',
              Component: Authentication,
              path: ':authMode',
              loader: withValidRoute(authLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_weekly_games',
          path: AppRoutes.WeeklyGames,
          children: [
            {
              id: 'route_weekly_games_index',
              index: true,
              Component: WeeklyGamesIndexRoute,
            },
            {
              id: 'route_weekly_games_year_week',
              path: ':yearId/:weekId',
              Component: WeeklyGames,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(weeklyGamesLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_standings',
          path: AppRoutes.Standings,
          children: [
            {
              id: 'route_standings_index',
              index: true,
              Component: StandingsIndexRoute,
            },
            {
              id: 'route_standings_year_week',
              path: ':yearId/:weekId',
              Component: Standings,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(standingsLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_view_team',
          path: AppRoutes.ViewTeam,
          children: [
            {
              id: 'route_view_team_index',
              index: true,
              Component: ViewTeamIndexRoute,
            },
            {
              id: 'route_view_team_season',
              path: ':yearId/:teamId',
              Component: ViewTeam,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(viewTeamLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_category_rankings',
          path: AppRoutes.CategoryRankings,
          children: [
            {
              id: 'route_category_rankings_index',
              index: true,
              Component: CategoryRankingsIndexRoute,
            },
            {
              id: 'route_category_rankings_year_week',
              path: ':yearId/:weekId',
              Component: CategoryRankings,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(categoryRankingsLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_comparison',
          path: AppRoutes.Comparison,
          children: [
            {
              id: 'route_comparison_index',
              index: true,
              Component: ComparisonIndexRoute,
            },
            {
              id: 'route_comparison_year',
              path: ':yearId',
              Component: CompareTeams,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(comparisonLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_head_to_head',
          path: AppRoutes.HeadToHead,
          Component: HeadToHeadScene,
          hydrateFallbackElement: (
            <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
          ),
        },
        {
          id: 'route_team_stats',
          path: AppRoutes.TeamStats,
          children: [
            {
              id: 'route_team_stats_index',
              index: true,
              Component: TeamStatsIndexRoute,
            },
            {
              id: 'route_team_stats_year_week',
              path: ':yearId/:weekId',
              Component: TeamStats,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(teamStatsLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_team',
          path: AppRoutes.Team,
          children: [
            {
              id: 'route_team_index',
              index: true,
              Component: TeamIndexRoute,
            },
            {
              id: 'route_team_year_week_team',
              path: ':yearId/:weekId/:teamId',
              Component: Team,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(teamLoader, getAvailableSeasons),
            },
          ],
        },
        {
          id: 'route_game_details',
          path: AppRoutes.GameDetails,
          children: [
            {
              id: 'route_game_details_index',
              index: true,
              Component: GameDetailsIndexRoute,
            },
            {
              id: 'route_game_details_gameId',
              path: ':gameId',
              Component: GameDetails,
              hydrateFallbackElement: (
                <ASLoader size={ComponentSize.Large} loaderType={LoaderType.Spin} showLoader={true} />
              ),
              loader: withValidRoute(gameDetailsLoader, getAvailableSeasons),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <App />,
    },
  ],
  {
    basename: '/',
    // future: { v7_startTransition: true },
  },
);

export default router;
